import { DateTimeError } from "./errors.js";
import { addCalendarDays, epochMilliseconds, parseCalendarDate, parseCalendarTiming, parseInstant, parseLocalTime, parseTimeZone } from "./validation.js";
import { systemClock } from "./clock.js";
const styles = /* @__PURE__ */ new Set(["full", "long", "medium", "short"]);
const locales = /* @__PURE__ */ new Map();
const hourLocales = /* @__PURE__ */ new Map();
function locale(value) {
  if (typeof value !== "string" || !value) throw new DateTimeError("INVALID_DISPLAY_CONTEXT", "A locale is required.");
  const cached = locales.get(value);
  if (cached) return cached;
  try {
    const canonical = Intl.getCanonicalLocales(value)[0];
    if (locales.size >= 64) locales.delete(locales.keys().next().value);
    locales.set(value, canonical);
    return canonical;
  } catch {
    throw new DateTimeError("INVALID_DISPLAY_CONTEXT", `Invalid locale: ${value}.`);
  }
}
function context(value) {
  if (!value || !["h12", "h23"].includes(value.hourCycle)) {
    throw new DateTimeError("INVALID_DISPLAY_CONTEXT", "Display context requires locale, timezone and h12/h23 hour cycle.");
  }
  return { locale: locale(value.locale), timeZone: parseTimeZone(value.timeZone), hourCycle: value.hourCycle };
}
function style(value) {
  if (!styles.has(value)) throw new DateTimeError("INVALID_DISPLAY_CONTEXT", `Unsupported display style: ${value}.`);
}
function localeWithHourCycle(localeName, hourCycle) {
  const key = `${localeName}|${hourCycle}`, cached = hourLocales.get(key);
  if (cached) return cached;
  const parts = localeName.split("-");
  const privateIndex = parts.indexOf("x");
  const unicodeIndex = parts.findIndex((part, index) => part === "u" && (privateIndex < 0 || index < privateIndex));
  if (unicodeIndex < 0) parts.splice(privateIndex < 0 ? parts.length : privateIndex, 0, "u", "hc", hourCycle);
  else {
    let end = unicodeIndex + 1;
    while (end < parts.length && parts[end].length > 1) end++;
    const existing = parts.indexOf("hc", unicodeIndex + 1);
    if (existing >= 0 && existing < end) {
      let stop = existing + 1;
      while (stop < end && parts[stop].length > 2) stop++;
      parts.splice(existing, stop - existing);
      end -= stop - existing;
    }
    let firstKey = unicodeIndex + 1;
    while (firstKey < end && parts[firstKey].length > 2) firstKey++;
    parts.splice(firstKey, 0, "hc", hourCycle);
  }
  const result = Intl.getCanonicalLocales(parts.join("-"))[0];
  if (hourLocales.size >= 64) hourLocales.delete(hourLocales.keys().next().value);
  hourLocales.set(key, result);
  return result;
}
function resolveDisplayContext(preference, device) {
  const selected = preference || { timeZoneMode: "device", hourCycle: "h12" };
  if (!["device", "fixed"].includes(selected.timeZoneMode) || !["system", "h12", "h23"].includes(selected.hourCycle || "system") || selected.timeZoneMode === "device" && selected.timeZone != null) {
    throw new DateTimeError("INVALID_DISPLAY_CONTEXT", "Invalid display preference.");
  }
  return context({
    ...device,
    timeZone: selected.timeZoneMode === "fixed" ? selected.timeZone : device.timeZone,
    hourCycle: selected.hourCycle === "system" ? device.hourCycle : selected.hourCycle || "h12"
  });
}
function createDateTimeFormatter(maxCacheSize = 64) {
  if (!Number.isInteger(maxCacheSize) || maxCacheSize < 1 || maxCacheSize > 256) {
    throw new DateTimeError("INVALID_DISPLAY_CONTEXT", "Formatter cache size must be between 1 and 256.");
  }
  const cache = /* @__PURE__ */ new Map();
  function get(localeName, options) {
    const key = JSON.stringify([localeName, options]);
    const cached = cache.get(key);
    if (cached) {
      cache.delete(key);
      cache.set(key, cached);
      return cached;
    }
    const formatter = new Intl.DateTimeFormat(localeName, options);
    if (cache.size >= maxCacheSize) cache.delete(cache.keys().next().value);
    cache.set(key, formatter);
    return formatter;
  }
  function formatInstant2(value, display, options = { dateStyle: "medium", timeStyle: "short" }) {
    const selected = context(display);
    if (!options || typeof options !== "object" || Object.keys(options).some((key) => !["dateStyle", "timeStyle"].includes(key))) {
      throw new DateTimeError("INVALID_DISPLAY_CONTEXT", "Only dateStyle/timeStyle are allowed; timezone and hour cycle belong to the display context.");
    }
    if (!options.dateStyle && !options.timeStyle) throw new DateTimeError("INVALID_DISPLAY_CONTEXT", "Specify a date or time display style.");
    if (options.dateStyle) style(options.dateStyle);
    if (options.timeStyle) style(options.timeStyle);
    return get(localeWithHourCycle(selected.locale, selected.hourCycle), { ...options, timeZone: selected.timeZone, hourCycle: selected.hourCycle, calendar: "gregory" }).format(epochMilliseconds(value)).replace(/\b(?:AM|PM)\b/g, (period) => period.toLowerCase());
  }
  function formatCalendarDate2(value, localeName, dateStyle = "medium") {
    parseCalendarDate(value);
    style(dateStyle);
    return get(locale(localeName), { dateStyle, timeZone: "UTC", calendar: "gregory" }).format(/* @__PURE__ */ new Date(`${value}T12:00:00.000Z`));
  }
  function calendarDateAtInstant2(value, timeZone) {
    const parts = get("en-CA", { timeZone: parseTimeZone(timeZone), calendar: "gregory", numberingSystem: "latn", year: "numeric", month: "2-digit", day: "2-digit", era: "short" }).formatToParts(epochMilliseconds(value));
    const part = (name) => parts.find((item) => item.type === name).value;
    if (part("era") !== "AD") throw new DateTimeError("INVALID_DATE", "The local date is outside the supported Gregorian years 0001\u20139999.");
    return parseCalendarDate(`${part("year").padStart(4, "0")}-${part("month")}-${part("day")}`);
  }
  function calendarDayCategory2(value, timeZone, clock = systemClock) {
    const date = calendarDateAtInstant2(value, timeZone), today = calendarDateAtInstant2(clock.now(), timeZone);
    if (date === today) return "today";
    if (date < today && addCalendarDays(date, 1) === today) return "yesterday";
    if (date > today && addCalendarDays(today, 1) === date) return "tomorrow";
    return "other";
  }
  function formatEventTime2(value, eventTimeZone, display, options) {
    const selected = context(display), saved = parseTimeZone(eventTimeZone);
    const viewerLabel = formatInstant2(value, selected, options);
    const equivalent = get("en", { timeZone: selected.timeZone }).resolvedOptions().timeZone === get("en", { timeZone: saved }).resolvedOptions().timeZone;
    return {
      viewerLabel,
      viewerTimeZone: selected.timeZone,
      eventTimeZone: saved,
      eventLabel: equivalent ? null : formatInstant2(value, { ...selected, timeZone: saved }, options)
    };
  }
  return Object.freeze({
    formatInstant: formatInstant2,
    formatCalendarDate: formatCalendarDate2,
    calendarDateAtInstant: calendarDateAtInstant2,
    calendarDayCategory: calendarDayCategory2,
    formatEventTime: formatEventTime2,
    clearCache: () => cache.clear(),
    cacheSize: () => cache.size
  });
}
const sharedFormatter = createDateTimeFormatter();
const { formatInstant, formatCalendarDate, calendarDateAtInstant, calendarDayCategory, formatEventTime } = sharedFormatter;
function occurrencePosition(value, display, now = systemClock.now()) {
  if (!formatOccurrence(value, display)) return null;
  const instantNow = parseInstant(now);
  const today = calendarDateAtInstant(instantNow, display.timeZone);
  if (value.calendarTiming != null) {
    const calendar = parseCalendarTiming(value.calendarTiming);
    const past = calendar.kind === "allDay" ? calendar.endDateExclusive <= today : calendar.startDate < today;
    return {
      past,
      upcoming: !past,
      current: !past && calendar.startDate <= today,
      sortKey: `${calendar.startDate}:0`,
      startDate: calendar.startDate
    };
  }
  const start = parseInstant(value.startsAt);
  const current = start <= instantNow && Boolean(value.endsAt && parseInstant(value.endsAt) > instantNow);
  const startDate = calendarDateAtInstant(start, display.timeZone);
  return {
    past: start < instantNow && !current,
    upcoming: start >= instantNow || current,
    current,
    sortKey: `${startDate}:1:${start}`,
    startDate
  };
}
function readDeviceDisplayContext() {
  const resolved = new Intl.DateTimeFormat(void 0, { hour: "numeric" }).resolvedOptions();
  return context({
    locale: resolved.locale,
    timeZone: parseTimeZone(resolved.timeZone),
    hourCycle: resolved.hourCycle === "h11" || resolved.hourCycle === "h12" ? "h12" : "h23"
  });
}
function formatOccurrence(value, display, options = {}) {
  if (!value || value.timingVersion === void 0) return null;
  if (value.timingVersion !== 1) throw new DateTimeError("INVALID_DISPLAY_CONTEXT", "Unsupported occurrence timing version.");
  const selected = context(display);
  const dateStyle = options.dateStyle || "long";
  if (value.calendarTiming != null) {
    const calendar = parseCalendarTiming(value.calendarTiming);
    const start2 = formatCalendarDate(calendar.startDate, selected.locale, dateStyle);
    const last = calendar.kind === "allDay" ? addCalendarDays(calendar.endDateExclusive, -1) : calendar.startDate;
    const dateLabel = last === calendar.startDate ? start2 : `${start2} \u2013 ${formatCalendarDate(last, selected.locale, dateStyle)}`;
    const timeLabel = calendar.kind === "allDay" ? "All day" : calendar.kind === "tbc" ? "Time to be confirmed" : "Date only";
    return { kind: calendar.kind, dateLabel, timeLabel, viewerLabel: `${dateLabel} \xB7 ${timeLabel}`, eventLabel: null };
  }
  const start = parseInstant(value.startsAt), zone = parseTimeZone(value.timeZone);
  const end = value.endsAt == null ? null : parseInstant(value.endsAt);
  if (end && epochMilliseconds(end) <= epochMilliseconds(start)) throw new DateTimeError("INVALID_DISPLAY_CONTEXT", "Occurrence end must follow its start.");
  const range = (inZone) => {
    const dateLabel = formatInstant(start, inZone, { dateStyle });
    const startTime = formatInstant(start, inZone, { timeStyle: "short" });
    const endLabel = end && options.includeEndTime !== false ? formatInstant(end, inZone, calendarDateAtInstant(start, inZone.timeZone) === calendarDateAtInstant(end, inZone.timeZone) ? { timeStyle: "short" } : { dateStyle: "long", timeStyle: "short" }) : null;
    const timeLabel = endLabel ? `${startTime} \u2013 ${endLabel}` : startTime;
    return { dateLabel, timeLabel, viewerLabel: `${dateLabel} \xB7 ${timeLabel} (${inZone.timeZone})` };
  };
  const viewer = range(selected);
  const differs = formatEventTime(start, zone, selected, { timeStyle: "short" }).eventLabel !== null;
  return { kind: "timed", ...viewer, eventLabel: differs ? range({ ...selected, timeZone: zone }).viewerLabel : null };
}
const record = (value) => value && typeof value === "object" ? value : {};
const hasAddress = (value) => typeof value === "string" ? Boolean(value.trim()) : Object.values(record(value)).some((part) => typeof part === "string" && Boolean(part.trim()));
function attendance(value) {
  const source = value || {};
  const explicit = String(source.locationMode || "").toLowerCase();
  let physical = explicit === "inperson" || explicit === "both", online = explicit === "online" || explicit === "both";
  const physicalZones = [];
  const venues = [source.venue, ...Array.isArray(source.venues) ? source.venues : [], typeof source.location === "object" ? source.location : null];
  for (const entry of venues) {
    const outer = record(entry), venue = typeof outer.venue === "object" ? record(outer.venue) : outer;
    const type = String(venue.type || "").toLowerCase();
    const remote = type.includes("online") || type.includes("virtual");
    const inPerson = !remote && (Boolean(type) || hasAddress(venue.address)) || type === "both";
    physical || (physical = inPerson);
    online || (online = remote || type === "both" || Boolean(venue.onlineLink));
    if (inPerson && typeof venue.timeZone === "string") physicalZones.push(venue.timeZone);
  }
  online || (online = Boolean(source.onlineLink));
  physical || (physical = hasAddress(source.address) || !online && typeof source.location === "string" && Boolean(source.location.trim()) && !/^https?:\/\//i.test(source.location));
  if (explicit === "online") physical = false;
  if (explicit === "inperson") online = false;
  if (physical && typeof source.venueTimeZone === "string") physicalZones.push(source.venueTimeZone);
  return { mode: physical ? online ? "hybrid" : "inPerson" : online ? "online" : "unknown", physicalZones };
}
function attendanceContext(value, fallback) {
  const own = attendance(value);
  return own.mode === "unknown" ? attendance(fallback) : own;
}
function attendanceMode(value, fallback) {
  return attendanceContext(value, fallback).mode;
}
function venueSchedulingTimeZone(venue) {
  for (const zone of attendance({ venue }).physicalZones) {
    try {
      return parseTimeZone(zone);
    } catch {
    }
  }
  return void 0;
}
function occurrenceClock(value, display, location) {
  const source = attendanceContext(value, location);
  if (source.mode === "inPerson" || source.mode === "hybrid") {
    for (const zone of [...source.physicalZones, value.timeZone]) {
      if (!zone) continue;
      try {
        return { ...display, timeZone: parseTimeZone(zone) };
      } catch {
      }
    }
  }
  return display;
}
function formatOccurrenceSummary(value, display, location, options = {}) {
  const clock = value ? occurrenceClock(value, display, location) : display;
  const timing = formatOccurrence(value, clock, options);
  if (!timing || !value) return null;
  const date = timing.kind === "timed" ? epochMilliseconds(parseInstant(value.startsAt)) : /* @__PURE__ */ new Date(`${parseCalendarTiming(value.calendarTiming).startDate}T12:00:00Z`);
  const weekdayLabel = new Intl.DateTimeFormat(clock.locale, {
    weekday: "long",
    timeZone: timing.kind === "timed" ? clock.timeZone : "UTC",
    calendar: "gregory"
  }).format(date);
  return { ...timing, weekdayLabel, viewerLabel: `${timing.dateLabel} \xB7 ${timing.timeLabel}`, eventLabel: null };
}
function timeZoneLabel(value) {
  const zone = parseTimeZone(value);
  return zone === "UTC" ? "UTC" : zone.split("/").slice(1).join(" / ").replace(/_/g, " ");
}
function timeZoneOptions(current = "", { showUtcOffset = false, clock = systemClock } = {}) {
  const australian = [
    "Australia/Adelaide",
    "Australia/Brisbane",
    "Australia/Darwin",
    "Australia/Eucla",
    "Australia/Hobart",
    "Australia/Lord_Howe",
    "Australia/Melbourne",
    "Australia/Perth",
    "Australia/Sydney"
  ];
  const fallback = [...australian, "Pacific/Auckland", "Asia/Kathmandu", "Asia/Singapore", "Europe/London", "America/New_York", "America/Los_Angeles"];
  const runtime = Intl;
  let supported = fallback;
  try {
    supported = runtime.supportedValuesOf?.("timeZone") || fallback;
  } catch {
  }
  const reference = showUtcOffset ? epochMilliseconds(parseInstant(clock.now())) : null;
  return [.../* @__PURE__ */ new Set([...australian, "UTC", ...supported, ...current ? [current] : []])].flatMap((value) => {
    try {
      const zone = parseTimeZone(value);
      let label = value === "UTC" ? "UTC" : `${timeZoneLabel(value)} \xB7 ${value.split("/")[0]}`;
      if (reference !== null) {
        const offset = new Intl.DateTimeFormat("en-AU", { timeZone: zone, timeZoneName: "longOffset" }).formatToParts(reference).find((part) => part.type === "timeZoneName").value;
        label += ` (${offset === "GMT" ? "UTC+00:00" : offset.replace(/^GMT/, "UTC")})`;
      }
      return [{ value: zone, label }];
    } catch {
      return [];
    }
  });
}
function resolveCreationTimeZone({ activityTimeZone, venueTimeZone, communityTimeZone, device }) {
  let deviceZone = null;
  try {
    deviceZone = (device === void 0 ? readDeviceDisplayContext() : device)?.timeZone || null;
  } catch {
  }
  for (const value of [activityTimeZone, venueTimeZone, deviceZone, communityTimeZone]) {
    if (!value) continue;
    try {
      return parseTimeZone(value);
    } catch {
    }
  }
  return "";
}
function occurrenceTimeZoneDetail(value, display, location) {
  if (!value || value.timingVersion !== 1 || !value.timeZone) return null;
  if (value.calendarTiming != null && parseCalendarTiming(value.calendarTiming).kind === "dateOnly") return null;
  const zone = occurrenceClock(value, { ...display, timeZone: parseTimeZone(value.timeZone) }, location).timeZone;
  const timing = formatOccurrence(value, { ...display, timeZone: zone });
  const viewer = formatOccurrence(value, display);
  const date = viewer.dateLabel !== timing.dateLabel ? `${timing.dateLabel} \xB7 ` : "";
  const place = attendanceMode(value, location) === "online" ? `Scheduled in ${timeZoneLabel(zone)} time` : timeZoneLabel(zone);
  return {
    timeZone: zone,
    label: `${place} \xB7 ${date}${timing.timeLabel}`,
    description: `${zone} \xB7 ${timing.dateLabel} \xB7 ${timing.timeLabel}`
  };
}
function scheduleTimeZoneDetail(schedule, display, location) {
  if (!schedule) return null;
  const zone = parseTimeZone(schedule.timeZone), time = parseLocalTime(schedule.startTime);
  const clockLabel = formatInstant(parseInstant(`2000-01-01T${time}:00Z`), { ...display, timeZone: parseTimeZone("UTC") }, { timeStyle: "short" });
  const place = attendanceMode(location) === "online" ? `Scheduled in ${timeZoneLabel(zone)} time` : timeZoneLabel(zone);
  return { timeZone: zone, label: `${place} \xB7 ${clockLabel}`, description: `${zone} \xB7 ${clockLabel} in the activity's regular schedule` };
}
function timeZoneTooltip({ occurrence, schedule, display, location }) {
  const detail = occurrence ? occurrenceTimeZoneDetail(occurrence, display, location) : scheduleTimeZoneDetail(schedule, display, location);
  if (!detail) return null;
  const calendar = occurrence?.calendarTiming == null ? null : parseCalendarTiming(occurrence.calendarTiming);
  const date = calendar?.startDate || schedule?.startDate;
  const reference = new Date(occurrence?.startsAt || (date ? `${date}T12:00:00Z` : systemClock.now()));
  const part = (timeZone, timeZoneName) => {
    const parts = new Intl.DateTimeFormat("en-AU", { timeZone, timeZoneName }).formatToParts(reference);
    const start = parts.findIndex((value) => value.type === "timeZoneName");
    let end = parts.length - 1;
    while (end > start && parts[end].type !== "timeZoneName") end--;
    return parts.slice(start, end + 1).map((value) => value.value).join("");
  };
  if (part(display.timeZone, "longOffset") === part(detail.timeZone, "longOffset")) return null;
  const rawOffset = part(detail.timeZone, "longOffset");
  const offset = ["GMT", "UTC"].includes(rawOffset) ? "UTC+0" : rawOffset.replace(/^GMT/, "UTC").replace(/^UTC([+-])0(\d)/, "UTC$1$2").replace(/:00$/, "");
  return `Timezone: ${timeZoneLabel(detail.timeZone)} \xB7 ${part(detail.timeZone, "short")} (${offset})`;
}
function occurrenceViewerTimeDetail(value, display, location) {
  if (!value || !["inPerson", "hybrid"].includes(attendanceMode(value, location))) return null;
  const main = formatOccurrenceSummary(value, display, location), viewer = formatOccurrence(value, display);
  if (!main || !viewer || main.kind !== "timed" || main.viewerLabel === `${viewer.dateLabel} \xB7 ${viewer.timeLabel}`) return null;
  return `Your time: ${viewer.dateLabel} \xB7 ${viewer.timeLabel}`;
}
function formatVenueBookingWindow(value, display) {
  if (!value.bookingStartsAt || !value.bookingEndsAt) return null;
  const timeZone = value.bookingTimeZone || display.timeZone;
  const summary = formatOccurrenceSummary({
    timingVersion: 1,
    startsAt: value.bookingStartsAt,
    endsAt: value.bookingEndsAt,
    timeZone
  }, display, { venues: [{ type: "CommunityVenue", timeZone }] });
  return summary ? `${summary.dateLabel} \xB7 ${summary.timeLabel}` : null;
}
export {
  attendanceMode,
  calendarDateAtInstant,
  calendarDayCategory,
  createDateTimeFormatter,
  formatCalendarDate,
  formatEventTime,
  formatInstant,
  formatOccurrence,
  formatOccurrenceSummary,
  formatVenueBookingWindow,
  occurrencePosition,
  occurrenceTimeZoneDetail,
  occurrenceViewerTimeDetail,
  readDeviceDisplayContext,
  resolveCreationTimeZone,
  resolveDisplayContext,
  scheduleTimeZoneDetail,
  timeZoneLabel,
  timeZoneOptions,
  timeZoneTooltip,
  venueSchedulingTimeZone
};
//# sourceMappingURL=display.js.map
